package com.milta.feature.admin.data.remote

import com.milta.core.network.ApiException
import com.milta.core.network.Endpoints
import com.milta.core.network.baseUrl
import com.milta.feature.admin.data.remote.dto.AdminAccountsResponseDto
import com.milta.feature.admin.data.remote.dto.AdminClaudeUsageEventsResponseDto
import com.milta.feature.admin.data.remote.dto.AdminClaudeUsageResponseDto
import com.milta.feature.admin.data.remote.dto.AdminOcrAnalysisResponseDto
import com.milta.feature.admin.data.remote.dto.AdminPromptDto
import com.milta.feature.admin.data.remote.dto.AdminPromptsResponseDto
import com.milta.feature.admin.data.remote.dto.UpdatePromptRequestDto
import com.milta.feature.admin.domain.entity.AdminClaudeUsagePeriod
import com.milta.feature.auth.data.remote.dto.ApiError
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.http.isSuccess

class AdminApi(private val client: HttpClient) {

    /** GET /api/v1/admin/accounts — Role.Admin only, read-only, cross-tenant. */
    suspend fun getAccounts(): AdminAccountsResponseDto {
        val response = client.get("$baseUrl/${Endpoints.adminAccounts}")
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
        return response.body()
    }

    /** GET /api/v1/admin/prompts — the 5 live Claude prompts. */
    suspend fun getPrompts(): AdminPromptsResponseDto {
        val response = client.get("$baseUrl/${Endpoints.adminPrompts}")
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
        return response.body()
    }

    /** PUT /api/v1/admin/prompts/{key} — overrides the editable body only. */
    suspend fun updatePrompt(key: String, body: String): AdminPromptDto {
        val response = client.put("$baseUrl/${Endpoints.adminPrompt(key)}") {
            contentType(ContentType.Application.Json)
            setBody(UpdatePromptRequestDto(body))
        }
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
        return response.body()
    }

    /** POST /api/v1/admin/prompts/{key}/reset — deletes the override, reverts to the default. */
    suspend fun resetPrompt(key: String): AdminPromptDto {
        val response = client.post("$baseUrl/${Endpoints.adminPromptReset(key)}")
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
        return response.body()
    }

    /** GET /api/v1/admin/ocr-analysis — background OCR analysis pipeline cost-comparison view. */
    suspend fun getOcrAnalysis(): AdminOcrAnalysisResponseDto {
        val response = client.get("$baseUrl/${Endpoints.adminOcrAnalysis}")
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
        return response.body()
    }

    /** GET /api/v1/admin/claude-usage — real per-account/per-organisation token consumption. */
    suspend fun getClaudeUsage(period: AdminClaudeUsagePeriod): AdminClaudeUsageResponseDto {
        val response = client.get("$baseUrl/${Endpoints.adminClaudeUsage}") {
            parameter("period", period.name)
        }
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
        return response.body()
    }

    /** GET /api/v1/admin/claude-usage/accounts/{accountId}/events — paginated per-call drill-down. */
    suspend fun getClaudeUsageEvents(
        accountId: String,
        period: AdminClaudeUsagePeriod,
        page: Int,
        pageSize: Int,
    ): AdminClaudeUsageEventsResponseDto {
        val response = client.get("$baseUrl/${Endpoints.adminClaudeUsageEvents(accountId)}") {
            parameter("period", period.name)
            parameter("page", page)
            parameter("pageSize", pageSize)
        }
        if (!response.status.isSuccess()) {
            val apiError = runCatching { response.body<ApiError>() }.getOrNull()
            val message = apiError?.code ?: "error_${response.status.value}"
            throw ApiException(response.status.value, message)
        }
        return response.body()
    }
}
